// PATH: src/pages/StudentsListPage.tsx — liste des élèves (recherche, filtres, pagination)
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Empty, Input, Popconfirm, Select, Space, Tag, Typography, message } from "antd";
import { PlusOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import { useAuth } from "../context/AuthContext";
import { useCanEditStudents } from "../hooks/useCanEditStudents";
import PageHeader from "../components/PageHeader";
import StudentAvatar from "../components/StudentAvatar";
import ResponsiveTable from "../components/ResponsiveTable";
import { useSchoolContext } from "../hooks/useSchoolContext";
import { COLORS, FONT_MONO } from "../theme";
import type { SchoolClass, SchoolYear, Student } from "../models/school";

const StudentsListPage: React.FC = () => {
  const api = useSchoolApi();
  const navigate = useNavigate();
  // `userTypeCode` est le nom réellement renvoyé par GET /me de tc_identity.
  const isDirection = useAuth().user?.userTypeCode === "BUSINESS";
  // Inscrire : direction ou surveillance générale — pas l'enseignant.
  const { canEdit } = useCanEditStudents();

  const [items, setItems] = useState<Student[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // searchText = ce qui est affiché ; q = ce qui est interrogé (différé).
  // Deux états distincts, sinon le champ ne suivrait pas « Réinitialiser ».
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string | undefined>("ACTIF");
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  // Année et classe partagées avec Pédagogie et Paiements : l'agent qui suit
  // une classe la retrouve d'un écran à l'autre (cf. useSchoolContext).
  const { yearId, classId, setYearId, setClassId } = useSchoolContext();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listStudents({ q, status, school_year_id: yearId, class_id: classId, page, page_size: pageSize })
      .then((res) => {
        setItems(res.items ?? []);
        setTotal(res.total);
      })
      .catch(() => message.error("Impossible de charger les élèves"))
      .finally(() => setLoading(false));
  }, [api, q, status, yearId, classId, page, pageSize]);

  useEffect(load, [load]);
  useEffect(() => {
    api.listYears().then(setYears).catch(() => undefined);
  }, [api]);

  // Changer de filtre, de page ou de recherche invalide la sélection : agir en
  // lot sur des élèves qu'on ne voit plus serait un piège (cf. Paiements).
  useEffect(() => setSelectedRowKeys([]), [q, status, yearId, classId, page, pageSize]);

  // runBulk applique une action à chaque élève sélectionné, tolère les échecs
  // partiels (Promise.allSettled) et rend le compte exact de ce qui a réussi.
  const runBulk = (action: (id: number) => Promise<unknown>, verb: string) => {
    const ids = selectedRowKeys.map(Number);
    if (ids.length === 0) return;
    setBulkLoading(true);
    Promise.allSettled(ids.map((id) => action(id)))
      .then((results) => {
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) message.success(`${ok} élève(s) ${verb}`);
        if (fail) message.error(`${fail} action(s) en échec`);
        setSelectedRowKeys([]);
        load();
      })
      .finally(() => setBulkLoading(false));
  };

  // Recherche différée : une requête par frappe saturerait l'API et ferait
  // clignoter la liste. 300 ms = seuil habituel avant que l'attente se sente.
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const onSearch = useCallback((value: string) => {
    setSearchText(value);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setQ(value);
      setPage(1);
    }, 300);
  }, []);
  // Le timer survivrait au démontage et tenterait un setState sur un composant
  // disparu.
  useEffect(() => () => clearTimeout(debounce.current), []);
  useEffect(() => {
    // La classe n'est plus vidée ici : useSchoolContext le fait quand l'année
    // CHANGE, sans effacer celle restaurée à l'ouverture de l'écran.
    if (yearId) {
      api.listClasses(yearId).then(setClasses).catch(() => undefined);
    } else {
      setClasses([]);
    }
  }, [api, yearId]);
  // Classe mémorisée devenue invalide (supprimée, ou d'une autre année).
  useEffect(() => {
    if (classId && classes.length && !classes.some((c) => c.id === classId)) setClassId(undefined);
  }, [classes, classId, setClassId]);

  // Des filtres actifs changent le sens d'une liste vide : « aucun élève » et
  // « aucun résultat » n'appellent pas la même action.
  const filtered = !!q || !!yearId || !!classId || status !== "ACTIF";

  return (
    <div>
      <PageHeader
        title="Élèves"
        subtitle={loading ? "Chargement…" : `${total} élève${total > 1 ? "s" : ""}`}
        action={
          <Space>
            {/* L'import de masse est gardé côté backend (compte direction) :
                l'exposer aux autres comptes ne mènerait qu'à un 403. */}
            {isDirection && (
              <Button icon={<UploadOutlined />} onClick={() => navigate("/students/import")}>
                Importer un fichier
              </Button>
            )}
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/students/new")}>
                Nouvelle inscription
              </Button>
            )}
          </Space>
        }
      />

      <Card styles={{ body: { padding: 16 } }} style={{ marginBottom: 16 }}>
        <Space wrap size={8}>
          <Input
            placeholder="Nom, prénom ou code Massar"
            allowClear
            prefix={<SearchOutlined style={{ color: "var(--app-muted)" }} />}
            style={{ width: 280 }}
            value={searchText}
            // Recherche à la frappe (différée) : l'ancien Input.Search
            // n'interrogeait qu'à la validation, et vider le champ ne
            // réinitialisait rien.
            onChange={(e) => onSearch(e.target.value)}
          />
          <Select
            style={{ width: 130 }}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: "ACTIF", label: "Actifs" },
              { value: "ARCHIVE", label: "Archivés" },
              { value: "", label: "Tous" },
            ]}
          />
          <Select
            placeholder="Année scolaire"
            allowClear
            style={{ width: 170 }}
            value={yearId}
            onChange={setYearId}
            options={years.map((y) => ({ value: y.id, label: y.label }))}
          />
          <Select
            placeholder="Classe"
            allowClear
            disabled={!yearId}
            style={{ width: 170 }}
            value={classId}
            onChange={setClassId}
            options={classes.map((c) => ({ value: c.id, label: c.label }))}
          />
          {filtered && (
            <Button
              type="link"
              onClick={() => {
                setSearchText("");
                setQ("");
                setStatus("ACTIF");
                setYearId(undefined);
                setClassId(undefined);
                setPage(1);
              }}
            >
              Réinitialiser
            </Button>
          )}
        </Space>
      </Card>

      <Card styles={{ body: { padding: 0 } }}>
        {/* Barre d'actions groupées : n'apparaît qu'avec une sélection. Les
            actions offertes suivent le filtre de statut — on n'archive pas des
            archivés, on ne restaure pas des actifs. */}
        {selectedRowKeys.length > 0 && (
          <div
            style={{
              padding: "10px 16px",
              borderBottom: "1px solid var(--app-border)",
              background: "var(--row-highlight)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Typography.Text strong>{selectedRowKeys.length} sélectionné(s)</Typography.Text>
            {canEdit && status !== "ARCHIVE" && (
              <Popconfirm
                title={`Archiver les ${selectedRowKeys.length} élève(s) sélectionné(s) ?`}
                okText="Archiver"
                cancelText="Annuler"
                onConfirm={() => runBulk(api.archiveStudent, "archivé(s)")}
              >
                <Button size="small" danger loading={bulkLoading}>Archiver</Button>
              </Popconfirm>
            )}
            {canEdit && status !== "ACTIF" && (
              <Button size="small" loading={bulkLoading} onClick={() => runBulk(api.restoreStudent, "restauré(s)")}>
                Restaurer
              </Button>
            )}
            <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>
              Effacer la sélection
            </Button>
          </div>
        )}
        <ResponsiveTable<Student>
          rowKey="id"
          loading={loading}
          dataSource={items}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          // Sur téléphone, une ligne de sept colonnes devient une carte :
          // identité, puis classe/Massar/statut en second plan.
          onRowClick={(s) => navigate(`/students/${s.id}`)}
          card={{
            title: (s) => `${s.last_name} ${s.first_name}`,
            extra: (s) => (
              <Tag bordered={false} color={s.status === "ACTIF" ? "success" : "default"}>
                {s.status === "ACTIF" ? "Actif" : "Archivé"}
              </Tag>
            ),
            description: (s) => (
              <>
                {s.massar_code && (
                  <Typography.Text type="secondary" style={{ fontFamily: FONT_MONO, fontSize: 12 }}>
                    {s.massar_code}
                  </Typography.Text>
                )}
                {s.city && <Typography.Text type="secondary">{s.city}</Typography.Text>}
                {s.birth_date && (
                  <Typography.Text type="secondary" style={{ fontFamily: FONT_MONO, fontSize: 12 }}>
                    {new Date(s.birth_date).toLocaleDateString("fr-MA")}
                  </Typography.Text>
                )}
              </>
            ),
          }}
          // La ligne entière est cliquable : viser le seul lien du nom est
          // inutilement précis pour une liste qu'on parcourt toute la journée.
          onRow={(s) => ({
            onClick: () => navigate(`/students/${s.id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtered ? "Aucun élève ne correspond aux filtres" : "Aucun élève inscrit"}
              >
                {!filtered && canEdit && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/students/new")}>
                    Nouvelle inscription
                  </Button>
                )}
              </Empty>
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t, [from, to]) => `${from}–${to} sur ${t}`,
            style: { padding: "0 16px" },
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          columns={[
            {
              title: "Élève",
              render: (_, s) => (
                <Space size={12}>
                  <StudentAvatar id={s.id} lastName={s.last_name} firstName={s.first_name} photoUrl={s.photo_url} />
                  <div style={{ lineHeight: 1.35 }}>
                    <Link
                      to={`/students/${s.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontWeight: 500, color: "var(--app-text)" }}
                    >
                      {s.last_name} {s.first_name}
                    </Link>
                    {/* L'état civil arabe n'est visible qu'ici : les documents
                        officiels s'appuient dessus, il doit être vérifiable. */}
                    {(s.last_name_ar || s.first_name_ar) && (
                      <div style={{ fontSize: 12, color: "var(--app-muted)" }} dir="rtl">
                        {[s.last_name_ar, s.first_name_ar].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </div>
                </Space>
              ),
            },
            {
              title: "Code Massar",
              dataIndex: "massar_code",
              width: 150,
              render: (v?: string) =>
                v ? (
                  <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>{v}</span>
                ) : (
                  <Dash />
                ),
            },
            {
              title: "Sexe",
              dataIndex: "gender",
              width: 80,
              render: (v?: string) => v || <Dash />,
            },
            {
              title: "Naissance",
              dataIndex: "birth_date",
              width: 130,
              render: (v?: string) =>
                v ? (
                  <span style={{ fontFamily: FONT_MONO, fontSize: 13 }}>
                    {new Date(v).toLocaleDateString("fr-MA")}
                  </span>
                ) : (
                  <Dash />
                ),
            },
            { title: "Ville", dataIndex: "city", render: (v?: string) => v || <Dash /> },
            {
              title: "Statut",
              dataIndex: "status",
              width: 110,
              // Pastille + libellé : le statut ne repose pas sur la seule couleur.
              render: (v: string) => (
                <Tag
                  bordered={false}
                  color={v === "ACTIF" ? "success" : "default"}
                  icon={
                    <span
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        marginRight: 6,
                        background: v === "ACTIF" ? COLORS.accent : COLORS.muted,
                      }}
                    />
                  }
                >
                  {v === "ACTIF" ? "Actif" : "Archivé"}
                </Tag>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

// Dash : valeur absente. Gris clair pour que les cellules vides reculent au
// lieu de rivaliser avec les données.
const Dash: React.FC = () => <span style={{ color: "var(--app-faint)" }}>—</span>;

export default StudentsListPage;
